import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams, useParams } from "react-router-dom";
import { ExternalLink, Search, SlidersHorizontal, X } from "lucide-react";
import { SEO } from "../components/SEO";
import { ShopActions } from "../components/ShopActions";
import { RelatedLinks } from "../components/RelatedLinks";
import { StorePhoto, storePhotoUrl } from "../components/StorePhoto";
import { api } from "../lib/api";
import { CATEGORY_INTROS } from "../lib/constants";

// Phase 6.5 — Map every category slug to a real store photo (or null = no photo backdrop)
const CATEGORY_HERO_PHOTO = {
    bourbon: "allocated-weller-bottle",
    whiskey: "allocated-weller-bottle",
    scotch: "allocated-weller-bottle",
    wine: "wine-aisle",
    beer: "beer-cave",
    seltzer: "beer-cave",
    liqueur: "liqueur-aisle",
    tequila: "liqueur-aisle",
    mezcal: "liqueur-aisle",
    vodka: "liqueur-aisle",
    gin: "liqueur-aisle",
    rum: "liqueur-aisle",
    cigars: null,
};

const CATS = [
    { id: "bourbon", label: "Bourbon" },
    { id: "whiskey", label: "Whiskey" },
    { id: "scotch", label: "Scotch" },
    { id: "tequila", label: "Tequila" },
    { id: "mezcal", label: "Mezcal" },
    { id: "vodka", label: "Vodka" },
    { id: "gin", label: "Gin" },
    { id: "rum", label: "Rum" },
    { id: "wine", label: "Wine" },
    { id: "beer", label: "Beer" },
    { id: "seltzer", label: "Seltzer" },
    { id: "liqueur", label: "Liqueur" },
    { id: "cigars", label: "Cigars" },
];

export default function Spirits() {
    const [params, setParams] = useSearchParams();
    const { category: paramCategory } = useParams();
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState(params.get("q") || "");

    const category = paramCategory || params.get("category") || "";
    const featured = params.get("featured") === "true";
    const sort = params.get("sort") || "name_asc";
    const page = parseInt(params.get("page") || "1", 10);
    const limit = 12;

    useEffect(() => {
        setLoading(true);
        const opts = { limit, skip: (page - 1) * limit, sort };
        if (category) opts.category = category;
        if (q) opts.q = q;
        if (featured) opts.featured = "true";
        api.products(opts)
            .then((d) => setData(d))
            .catch(() => setData({ items: [], total: 0 }))
            .finally(() => setLoading(false));
    }, [category, q, featured, sort, page]);

    const setParam = (key, val) => {
        const p = new URLSearchParams(params);
        if (val === "" || val === null || val === undefined || val === false) p.delete(key);
        else p.set(key, val);
        p.delete("page");
        setParams(p);
    };

    const totalPages = Math.max(1, Math.ceil(data.total / limit));
    const intro = category ? CATEGORY_INTROS[category] : null;
    const seoTitle = intro
        ? `${intro.eyebrow}`.replace("Curated in ", "").replace(" — ", " — ") + " · Liquor Studio"
        : category
            ? `${category.charAt(0).toUpperCase() + category.slice(1)} Selection`
            : "Browse Our Studio";
    const seoDesc = intro
        ? intro.lede.slice(0, 158)
        : "Browse the curated spirits, wine and craft beer selection at Liquor Studio in Clinton, Missouri.";

    return (
        <>
            <SEO
                title={seoTitle}
                description={seoDesc}
                path={category ? `/spirits/category/${category}` : "/spirits"}
                keywords={intro?.keywords}
            />

            {/* Category intro hero — only when filtering by category */}
            {intro && (
                <section
                    style={{ position: "relative", paddingTop: 140, paddingBottom: 64, borderBottom: "1px solid var(--ls-border)", background: CATEGORY_HERO_PHOTO[category] ? "#0a0a0a" : "var(--ls-panel-2)", overflow: "hidden" }}
                    data-testid="category-intro"
                    data-onscrim={CATEGORY_HERO_PHOTO[category] ? "true" : undefined}
                >
                    {CATEGORY_HERO_PHOTO[category] && (
                        <>
                            <StorePhoto
                                slug={CATEGORY_HERO_PHOTO[category]}
                                loading="eager"
                                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.85 }}
                                testid={`category-hero-${CATEGORY_HERO_PHOTO[category]}`}
                            />
                            <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.85) 100%)" }} />
                        </>
                    )}
                    <div className="ls-container" style={{ position: "relative" }}>
                        <div className="max-w-3xl" style={{ color: CATEGORY_HERO_PHOTO[category] ? "#fff" : undefined }}>
                            <span className="eyebrow" style={{ color: CATEGORY_HERO_PHOTO[category] ? "#F5B027" : undefined }}>{intro.eyebrow}</span>
                            <h1
                                className="font-display mt-4"
                                style={{ fontSize: "clamp(38px, 5.5vw, 64px)", fontWeight: 500, lineHeight: 1.05, letterSpacing: "-0.02em", color: CATEGORY_HERO_PHOTO[category] ? "#fff" : "var(--ls-text)", textShadow: CATEGORY_HERO_PHOTO[category] ? "0 2px 12px rgba(0,0,0,0.6)" : "none" }}
                                data-testid="category-intro-h1"
                            >
                                {intro.title}
                            </h1>
                            <p className="mt-6" style={{ fontSize: 18, lineHeight: 1.7, color: CATEGORY_HERO_PHOTO[category] ? "rgba(255,255,255,0.92)" : "var(--ls-text-secondary)" }}>
                                {intro.lede}
                            </p>
                            <div
                                className="mt-6"
                                style={{ fontSize: 15, lineHeight: 1.8, whiteSpace: "pre-wrap", color: CATEGORY_HERO_PHOTO[category] ? "rgba(255,255,255,0.88)" : "var(--ls-text-secondary)" }}
                                data-testid="category-intro-body"
                            >
                                {intro.body}
                            </div>
                            <div className="mt-8" style={{ maxWidth: 460 }}>
                                <ShopActions layout="row" testidPrefix="category-actions" showText={false} />
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {!intro && (
                <section style={{ paddingTop: 140, paddingBottom: 48, borderBottom: "1px solid var(--ls-border)" }}>
                    <div className="ls-container">
                        <span className="eyebrow">Spirits Catalog</span>
                        <h1 className="font-display mt-4 text-white" style={{ fontSize: "clamp(40px, 6vw, 72px)", fontWeight: 500, lineHeight: 1.02, letterSpacing: "-0.02em" }}>
                            Browse Our <em style={{ fontStyle: "italic", color: "var(--ls-gold)", fontWeight: 400 }}>Studio</em>
                        </h1>
                        <p className="mt-4" style={{ color: "var(--ls-text-secondary)", fontSize: 17 }}>
                            {data.total} curated bottles. Click any to view details, or shop on shop.liquor.studio.
                        </p>
                    </div>
                </section>
            )}

            <section className="py-12">
                <div className="ls-container">
                    <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-10">
                        {/* Sidebar */}
                        <aside data-testid="spirits-filters" style={{ background: "var(--ls-panel)", border: "1px solid var(--ls-border)", borderRadius: 6, padding: 24, alignSelf: "start" }}>
                            <div className="flex items-center gap-2 mb-5">
                                <SlidersHorizontal size={16} style={{ color: "var(--ls-gold)" }} />
                                <h2 className="eyebrow" style={{ margin: 0 }}>Filters</h2>
                            </div>

                            <div className="mb-6">
                                <label className="text-xs uppercase tracking-wider" style={{ color: "var(--ls-text-muted)" }}>
                                    Search
                                </label>
                                <div className="relative mt-2">
                                    <input
                                        type="text"
                                        value={q}
                                        onChange={(e) => setQ(e.target.value)}
                                        onKeyDown={(e) => e.key === "Enter" && setParam("q", q)}
                                        placeholder="Brand, name..."
                                        className="w-full text-white text-sm"
                                        style={{ background: "var(--ls-bg)", border: "1px solid var(--ls-border-strong)", borderRadius: 4, padding: "10px 12px 10px 36px" }}
                                        data-testid="spirits-search-input"
                                    />
                                    <Search size={14} style={{ position: "absolute", left: 12, top: 12, color: "var(--ls-text-muted)" }} />
                                </div>
                            </div>

                            <div className="mb-6">
                                <h3 className="text-xs uppercase tracking-wider mb-3" style={{ color: "var(--ls-text-muted)" }}>Category</h3>
                                <div className="flex flex-col gap-1.5">
                                    <button onClick={() => setParam("category", "")} className="text-left text-sm" style={{ color: !category ? "var(--ls-gold)" : "var(--ls-text-secondary)" }} data-testid="cat-all">
                                        All categories
                                    </button>
                                    {CATS.map((c) => (
                                        <button
                                            key={c.id}
                                            onClick={() => setParam("category", c.id)}
                                            className="text-left text-sm"
                                            style={{ color: category === c.id ? "var(--ls-gold)" : "var(--ls-text-secondary)" }}
                                            data-testid={`cat-${c.id}`}
                                        >
                                            {c.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="mb-6">
                                <label className="flex items-center gap-2 text-sm text-white">
                                    <input
                                        type="checkbox"
                                        checked={featured}
                                        onChange={(e) => setParam("featured", e.target.checked ? "true" : "")}
                                        data-testid="featured-toggle"
                                    />
                                    Featured only
                                </label>
                            </div>

                            <div>
                                <label className="text-xs uppercase tracking-wider" style={{ color: "var(--ls-text-muted)" }}>Sort</label>
                                <select
                                    value={sort}
                                    onChange={(e) => setParam("sort", e.target.value)}
                                    className="w-full text-white text-sm mt-2"
                                    style={{ background: "var(--ls-bg)", border: "1px solid var(--ls-border-strong)", borderRadius: 4, padding: 10 }}
                                    data-testid="spirits-sort"
                                >
                                    <option value="name_asc">Name A–Z</option>
                                    <option value="name_desc">Name Z–A</option>
                                    <option value="newest">Newest</option>
                                </select>
                            </div>
                        </aside>

                        {/* Grid */}
                        <div>
                            {loading ? (
                                <p style={{ color: "var(--ls-text-muted)" }}>Loading…</p>
                            ) : data.items.length === 0 ? (
                                <p style={{ color: "var(--ls-text-muted)" }} data-testid="spirits-empty">No products match your filters.</p>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="spirits-grid">
                                    {data.items.map((p) => (
                                        <article key={p.slug} className="cat-card" data-testid={`product-card-${p.slug}`}>
                                            <Link to={`/spirits/${p.slug}`} className="block">
                                                <div style={{ aspectRatio: "4/5", overflow: "hidden" }}>
                                                    <img src={p.image_url} alt={p.name} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                                </div>
                                                <div className="p-5">
                                                    <span className="pill" style={{ fontSize: 9, padding: "3px 8px" }}>{p.category}</span>
                                                    <h3 className="font-display text-white mt-3" style={{ fontSize: 19, fontWeight: 500, lineHeight: 1.2 }}>{p.name}</h3>
                                                    <p className="text-xs mt-1" style={{ color: "var(--ls-text-muted)" }}>{p.brand}</p>
                                                    <div className="flex items-center justify-between mt-3">
                                                        <span className="font-bebas" style={{ fontSize: 22, color: "var(--ls-gold)" }}>{p.price_display}</span>
                                                        <span className="text-xs" style={{ color: "var(--ls-text-secondary)" }}>View →</span>
                                                    </div>
                                                </div>
                                            </Link>
                                        </article>
                                    ))}
                                </div>
                            )}

                            {totalPages > 1 && (
                                <div className="flex justify-center items-center gap-2 mt-12" data-testid="spirits-pagination">
                                    {[...Array(totalPages)].map((_, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setParam("page", String(i + 1))}
                                            className="btn-ghost"
                                            style={{ padding: "6px 14px", fontSize: 13, color: page === i + 1 ? "var(--ls-gold)" : "white", borderColor: page === i + 1 ? "var(--ls-gold)" : "var(--ls-border-strong)" }}
                                        >
                                            {i + 1}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {/* Related internal-link cluster */}
                <div className="ls-container mt-12">
                    <RelatedLinks
                        title={category ? `More from ${category}` : "Continue browsing"}
                        eyebrow="Related"
                        testid="spirits-related-links"
                        items={[
                            { label: "Bourbon", to: "/spirits/category/bourbon", hint: "Allocated & store picks" },
                            { label: "Wine", to: "/spirits/category/wine", hint: "Old & New World" },
                            { label: "Craft Beer", to: "/spirits/category/beer", hint: "Regional & fresh" },
                            { label: "Tequila", to: "/spirits/category/tequila", hint: "100% blue agave" },
                            { label: "Cocktail Recipes", to: "/recipes", hint: "Make it tonight" },
                            { label: "Visit Our Store", to: "/visit", hint: "309 S 2nd St, Clinton" },
                        ]}
                    />
                </div>
            </section>
        </>
    );
}
